import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import useApiClient from '~/src/hooks/useApiClient';
import { snackbarMessageSelector } from '~/src/Snackbar/store';
import { Folder, pendingUpdateFolderState, currentFolderState } from '../store';
import {
  Entry,
  ProjectListResults,
  projectListState,
} from '~/src/Projects/store';
import {
  queryFilterSelector,
  SearchParams,
  searchStringState,
} from '~/src/FilterProjects/store';
import { ENTRIES_PER_PAGE } from '~/src/Projects/constants';

const useFolderUpdate = () => {
  const { t } = useTranslation('folders');
  const apiClient = useApiClient();
  const setPending = useSetRecoilState(pendingUpdateFolderState);
  const setSnackbarMessage = useSetRecoilState(snackbarMessageSelector);
  const setEntries = useSetRecoilState(projectListState);
  const currentFolder = useRecoilValue(currentFolderState);
  const filters = useRecoilValue(queryFilterSelector);
  const searchString = useRecoilValue(searchStringState);

  const updateFolder = async (
    folderId: number,
    body: Partial<Folder>,
    errorMessage?: string,
  ) => {
    setPending(true);
    try {
      await apiClient.patch<Folder>(`/folders/${folderId}`, body);

      const params: SearchParams = {
        limit: ENTRIES_PER_PAGE,
        skip: 0,
      };

      let requestFilters;

      switch (true) {
        case !!filters && !currentFolder:
          requestFilters = filters;
          break;
        case !filters && !!currentFolder:
          requestFilters = { folderId: currentFolder?._id };
          break;
        case !!filters && !!currentFolder:
          requestFilters = {
            folderId: currentFolder?._id,
            ...(filters || {}),
          };
          break;
        default:
          requestFilters = {};
      }

      params.filter = btoa(JSON.stringify(requestFilters));

      if (searchString?.value) {
        params.query = searchString?.value;
      }

      const {
        data: { data, pagination },
      } = await apiClient.get<{
        data: Entry[];
        pagination: {
          hasMore: boolean;
        };
      }>('/search', {
        params,
      });

      // If parentFolderId changed, refresh the entire list
      if (body.parentFolderId !== undefined) {
        setEntries(
          () => ({
            entries: data,
            hasMore: pagination?.hasMore || false,
          } as ProjectListResults),
        );
      } else {
        setEntries(
          (resultsState) => ({
            hasMore: pagination?.hasMore || false,
            entries: resultsState.entries.reduce((acc, item) => {
              if (item.project || item.folder?._id !== folderId) {
                acc.push(item);
                return acc;
              }
              const updatedItem = data.find(
                (d) => d.folder?._id === folderId,
              );
              if (updatedItem) {
                acc.push(updatedItem);
              }
              return acc;
            }, []),
          } as ProjectListResults),
        );
      }
    } catch (err) {
      setSnackbarMessage({
        message: errorMessage || t('errors.defaultError'),
        severity: 'error',
      });
      throw err;
    } finally {
      setPending(false);
    }
  };

  return [updateFolder];
};

export default useFolderUpdate;
